import type { FormattedDimensionLabels } from '@/domain/product/persistence/dimensionRules'
import {
  pickReadableProductText,
  type ProductLabelContext,
} from '@/domain/product/technicalLabel'
import type { InventoryRow } from '@/features/inventory/lib'
import type { OrderModalValues } from '@/types'
import {
  extractIdOrValue,
  extractLabel,
  toStringValue,
} from '@/utils/value.utils'
import { formatProductDimensions } from '@/utils/product.handler'

type AggregatedQuantityData = {
  'quantity.number'?: number
  'quantity.unit'?: string
}

export type OrderProductPropertyEntry = {
  key?: string
  values?: unknown
  type?: string
}

export type OrderProductLegacyProperties = Record<string, unknown>

export type OrderProductPropertyCollection =
  | OrderProductLegacyProperties
  | OrderProductPropertyEntry[]

export type OrderProductSource = Record<string, unknown> & {
  properties?: OrderProductPropertyCollection
  rawProduct?: OrderProductSource | null
}

export type SerializedOrderDimensions = string | FormattedDimensionLabels

export type OrderSelectionNodeData = OrderProductSource & {
  id: string
  productID: string
  name: string
  pictures: string[]
  quantity: {
    number: number
    unit: string
  }
  price: {
    amount?: number
    currency?: string
  }
  rawPrice?: number
  rawTotal?: number
  total?: number
  byUserID?: string
  organizationID?: string
  projectID?: string
  placeID?: string
  slug?: string
  action?: string
  nature?: string
  condition?: string
  provenance?: string
  category?: string
  brand?: string
  model?: string
  ref?: string
  dimensions?: SerializedOrderDimensions
  rawProduct?: OrderProductSource
}

export type SerializedOrderProduct = OrderSelectionNodeData & {
  price: { amount: number; currency: string }
  rawPrice: number
}

export type OrderSelectionNode = {
  group?: boolean
  level?: number
  childIndex?: number
  data?: OrderProductSource | null
  allLeafChildren?: Array<{
    data?: OrderProductSource | null
  } | null> | null
  parent?: {
    aggData?: AggregatedQuantityData
  } | null
}

export type OrderSelectionApi = {
  getSelectedNodes: () => OrderSelectionNode[]
}

export type OrderSelectionMode = 'quote' | 'request'

export type OrderModalItem = NonNullable<OrderModalValues['items']>[number]

export type SerializedOrderLineItem = {
  product: SerializedOrderProduct
  quantity: number
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

const normalizeString = (value: string | undefined): string | undefined => {
  if (!value) return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

const readTextValue = (value: unknown): string | undefined => {
  if (Array.isArray(value)) {
    const values = value
      .map((entry) => readTextValue(entry))
      .filter((entry): entry is string => Boolean(entry))
    if (values.length === 0) return undefined
    return normalizeString(values.join(', '))
  }

  const label = extractLabel(value)
  if (label) return normalizeString(label)

  if (isRecord(value)) {
    if (typeof value.short === 'string') {
      const shortValue = normalizeString(value.short)
      if (shortValue) return shortValue
    }
    if (typeof value.long === 'string') {
      const longValue = normalizeString(value.long)
      if (longValue) return longValue
    }
  }

  const scalar = toStringValue(value)
  return normalizeString(scalar)
}

const readIdentifierValue = (value: unknown): string | undefined => {
  if (Array.isArray(value)) {
    return readIdentifierValue(value[0])
  }

  const extracted = extractIdOrValue(value)
  if (extracted !== undefined) {
    return normalizeString(String(extracted))
  }

  return readTextValue(value)
}

const readNumberValue = (value: unknown): number | undefined => {
  if (Array.isArray(value)) {
    for (const entry of value) {
      const parsed = readNumberValue(entry)
      if (parsed !== undefined) return parsed
    }
    return undefined
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined
  }

  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }

  if (isRecord(value)) {
    if ('amount' in value) {
      const parsed = readNumberValue(value.amount)
      if (parsed !== undefined) return parsed
    }
    if ('number' in value) {
      const parsed = readNumberValue(value.number)
      if (parsed !== undefined) return parsed
    }
    if ('value' in value) {
      const parsed = readNumberValue(value.value)
      if (parsed !== undefined) return parsed
    }
    if ('label' in value) {
      const parsed = readNumberValue(value.label)
      if (parsed !== undefined) return parsed
    }
  }

  return undefined
}

const readPictures = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.flatMap((entry) => {
      if (typeof entry === 'string' && entry.length > 0) return [entry]
      return []
    })
  }

  if (typeof value === 'string' && value.length > 0) {
    return [value]
  }

  return []
}

const readPropertyValue = (
  source: OrderProductSource,
  key: string,
): unknown => {
  const properties = source.properties

  if (Array.isArray(properties)) {
    const entry = properties.find((candidate) => candidate?.key === key) as
      | OrderProductPropertyEntry
      | undefined
    return entry?.values
  }

  if (isRecord(properties) && key in properties) {
    return properties[key]
  }

  return undefined
}

const readPropertyTextValue = (
  source: OrderProductSource,
  key: string,
): string | undefined => readTextValue(readPropertyValue(source, key))

const collectFieldTextCandidates = (
  source: OrderProductSource,
  fallbackSource: OrderProductSource | null,
  key:
    | 'name'
    | 'condition'
    | 'provenance'
    | 'category'
    | 'brand'
    | 'model'
    | 'ref'
    | 'slug'
    | 'action'
    | 'nature',
): Array<string | undefined> => [
  readTextValue(source[key]),
  fallbackSource ? readTextValue(fallbackSource[key]) : undefined,
  readPropertyTextValue(source, key),
  fallbackSource ? readPropertyTextValue(fallbackSource, key) : undefined,
]

const readQuantityNumber = (source: OrderProductSource): number => {
  const quantity = source.quantity
  const directQuantity = readNumberValue(quantity)
  if (directQuantity !== undefined && !isRecord(quantity)) {
    return directQuantity
  }

  if (isRecord(quantity)) {
    const nestedQuantity = readNumberValue(quantity.number)
    if (nestedQuantity !== undefined) return nestedQuantity
  }

  const flatQuantity = readNumberValue(source['quantity.number'])
  if (flatQuantity !== undefined) return flatQuantity

  return 0
}

const readQuantityUnit = (source: OrderProductSource): string => {
  const quantity = source.quantity
  if (isRecord(quantity)) {
    const nestedUnit = readTextValue(quantity.unit)
    if (nestedUnit) return nestedUnit
  }

  const flatUnit = readTextValue(source['quantity.unit'])
  if (flatUnit) return flatUnit

  const propertyUnit = readPropertyTextValue(source, 'quantity.unit')
  if (propertyUnit) return propertyUnit

  const unit = readTextValue(source.unit)
  if (unit) return unit

  return 'unit'
}

const readPriceAmount = (source: OrderProductSource): number | undefined => {
  const directPrice = readNumberValue(source.price)
  if (directPrice !== undefined && !isRecord(source.price)) {
    return directPrice
  }

  if (isRecord(source.price)) {
    const nestedAmount = readNumberValue(source.price.amount)
    if (nestedAmount !== undefined) return nestedAmount
    if (isRecord(source.price.value)) {
      const valueAmount = readNumberValue(source.price.value.amount)
      if (valueAmount !== undefined) return valueAmount
    }
  }

  const flatAmount = readNumberValue(source['price.amount'])
  if (flatAmount !== undefined) return flatAmount

  const rawPrice = readNumberValue(source.rawPrice)
  if (rawPrice !== undefined) return rawPrice

  return undefined
}

const readPriceCurrency = (
  source: OrderProductSource,
  fallback?: string,
): string | undefined => {
  if (isRecord(source.price)) {
    const nestedCurrency = readTextValue(source.price.currency)
    if (nestedCurrency) return nestedCurrency
    if (isRecord(source.price.value)) {
      const valueCurrency = readTextValue(source.price.value.currency)
      if (valueCurrency) return valueCurrency
    }
  }

  const flatCurrency = readTextValue(source['price.currency'])
  if (flatCurrency) return flatCurrency

  const propertyCurrency = readPropertyTextValue(source, 'price.currency')
  if (propertyCurrency) return propertyCurrency

  return fallback
}

const stripNestedRawProduct = (
  source: OrderProductSource,
): OrderProductSource => {
  const clone = { ...source }
  delete clone.rawProduct
  return clone
}

const getSourceRawProduct = (
  source: OrderProductSource,
): OrderProductSource | null =>
  isRecord(source.rawProduct) ? stripNestedRawProduct(source.rawProduct) : null

const readFieldStringFromSources = (
  source: OrderProductSource,
  fallbackSource: OrderProductSource | null,
  key:
    | 'name'
    | 'condition'
    | 'provenance'
    | 'category'
    | 'brand'
    | 'model'
    | 'ref'
    | 'slug'
    | 'action'
    | 'nature',
  context: ProductLabelContext,
  options?: { allowTechnicalFallback?: boolean },
): string | undefined =>
  pickReadableProductText(
    collectFieldTextCandidates(source, fallbackSource, key),
    context,
    options,
  )

const readDimensionsValue = (
  source: OrderProductSource,
): SerializedOrderDimensions | undefined => {
  const dimensions = source.dimensions
  if (typeof dimensions === 'string') {
    return normalizeString(dimensions)
  }

  if (isRecord(dimensions) && typeof dimensions.label === 'string') {
    return dimensions as FormattedDimensionLabels
  }

  const formatted = formatProductDimensions(source)
  return formatted ?? undefined
}

const buildCanonicalRawProduct = (
  source: OrderProductSource,
  product: SerializedOrderProduct,
): OrderProductSource => {
  const rawProduct = stripNestedRawProduct(source)
  if (!product.slug) delete rawProduct.slug

  return {
    ...rawProduct,
    id: product.id,
    productID: product.productID,
    name: product.name,
    pictures: product.pictures,
    quantity: product.quantity,
    price: product.price,
    rawPrice: product.rawPrice,
    ...(product.rawTotal !== undefined ? { rawTotal: product.rawTotal } : {}),
    ...(product.total !== undefined ? { total: product.total } : {}),
    ...(product.byUserID ? { byUserID: product.byUserID } : {}),
    ...(product.organizationID
      ? { organizationID: product.organizationID }
      : {}),
    ...(product.projectID ? { projectID: product.projectID } : {}),
    ...(product.placeID ? { placeID: product.placeID } : {}),
    ...(product.slug ? { slug: product.slug } : {}),
    ...(product.action ? { action: product.action } : {}),
    ...(product.nature ? { nature: product.nature } : {}),
    ...(product.condition ? { condition: product.condition } : {}),
    ...(product.provenance ? { provenance: product.provenance } : {}),
    ...(product.category ? { category: product.category } : {}),
    ...(product.brand ? { brand: product.brand } : {}),
    ...(product.model ? { model: product.model } : {}),
    ...(product.ref ? { ref: product.ref } : {}),
    ...(product.dimensions ? { dimensions: product.dimensions } : {}),
  }
}

export const getOrderProductFieldString = (
  source: OrderProductSource,
  key:
    | 'name'
    | 'condition'
    | 'provenance'
    | 'category'
    | 'brand'
    | 'model'
    | 'ref'
    | 'slug'
    | 'action'
    | 'nature',
): string | undefined => {
  const rawProduct = getSourceRawProduct(source)
  const id =
    readIdentifierValue(source.id) ?? readIdentifierValue(rawProduct?.id) ?? ''
  const productID =
    readIdentifierValue(source.productID) ??
    readIdentifierValue(rawProduct?.productID) ??
    id

  return readFieldStringFromSources(source, rawProduct, key, {
    id,
    productID,
  })
}

export const getOrderProductDimensions = (
  source: OrderProductSource,
): SerializedOrderDimensions | undefined => {
  const rawProduct = getSourceRawProduct(source)

  return (
    readDimensionsValue(source) ??
    (rawProduct ? readDimensionsValue(rawProduct) : undefined)
  )
}

export const getOrderProductDimensionsLabel = (
  source: OrderProductSource,
): string | undefined => {
  const dimensions = getOrderProductDimensions(source)
  if (typeof dimensions === 'string') return normalizeString(dimensions)
  if (isRecord(dimensions) && typeof dimensions.label === 'string') {
    return normalizeString(dimensions.label)
  }
  return undefined
}

export const getOrderItemDimensionsLabel = (
  source: unknown,
): string | undefined => {
  if (!isRecord(source)) return undefined
  return getOrderProductDimensionsLabel(source as OrderProductSource)
}

export const buildCanonicalOrderProduct = (params: {
  source: OrderProductSource
  quantityNumber?: number
  quantityUnit?: string
  priceAmount?: number
  priceCurrency?: string
  fallbackPriceCurrency?: string
  rawTotal?: number
  total?: number
  fallbackByUserID?: string
}): SerializedOrderProduct => {
  const {
    source,
    quantityNumber,
    quantityUnit,
    priceAmount,
    priceCurrency,
    fallbackPriceCurrency,
    rawTotal,
    total,
    fallbackByUserID,
  } = params
  const directSource = stripNestedRawProduct(source)
  const baseSource = getSourceRawProduct(source) ?? directSource

  const id =
    readIdentifierValue(directSource.id) ??
    readIdentifierValue(baseSource.id) ??
    ''
  const productID =
    readIdentifierValue(directSource.productID) ??
    readIdentifierValue(baseSource.productID) ??
    id
  const nextQuantityNumber = quantityNumber ?? readQuantityNumber(directSource)
  const nextQuantityUnit = quantityUnit ?? readQuantityUnit(directSource)
  const nextPriceAmount = priceAmount ?? readPriceAmount(directSource) ?? 0
  const nextPriceCurrency =
    priceCurrency ||
    readPriceCurrency(directSource, fallbackPriceCurrency) ||
    'eur'
  const slug = readFieldStringFromSources(
    directSource,
    baseSource,
    'slug',
    {
      id,
      productID,
    },
    { allowTechnicalFallback: false },
  )
  const name =
    readFieldStringFromSources(
      directSource,
      baseSource,
      'name',
      {
        id,
        productID,
        slug,
      },
      { allowTechnicalFallback: false },
    ) ?? 'Nom manquant'
  const pictures = [
    ...new Set([
      ...readPictures(directSource.pictures),
      ...readPictures(directSource.picture),
      ...readPictures(baseSource.pictures),
      ...readPictures(baseSource.picture),
    ]),
  ]
  const condition = readFieldStringFromSources(
    directSource,
    baseSource,
    'condition',
    { id, productID, slug },
  )
  const provenance = readFieldStringFromSources(
    directSource,
    baseSource,
    'provenance',
    { id, productID, slug },
  )
  const category = readFieldStringFromSources(
    directSource,
    baseSource,
    'category',
    { id, productID, slug },
  )
  const brand = readFieldStringFromSources(directSource, baseSource, 'brand', {
    id,
    productID,
    slug,
  })
  const model = readFieldStringFromSources(directSource, baseSource, 'model', {
    id,
    productID,
    slug,
  })
  const ref = readFieldStringFromSources(directSource, baseSource, 'ref', {
    id,
    productID,
    slug,
  })
  const action = readFieldStringFromSources(
    directSource,
    baseSource,
    'action',
    {
      id,
      productID,
      slug,
    },
  )
  const nature = readFieldStringFromSources(
    directSource,
    baseSource,
    'nature',
    {
      id,
      productID,
      slug,
    },
  )
  const dimensions =
    readDimensionsValue(directSource) ?? readDimensionsValue(baseSource)
  const byUserID =
    readIdentifierValue(directSource.byUserID) ??
    readIdentifierValue(baseSource.byUserID) ??
    fallbackByUserID
  const organizationID =
    readIdentifierValue(directSource.organizationID) ??
    readIdentifierValue(baseSource.organizationID)
  const projectID =
    readIdentifierValue(directSource.projectID) ??
    readIdentifierValue(baseSource.projectID)
  const placeID =
    readIdentifierValue(directSource.placeID) ??
    readIdentifierValue(baseSource.placeID)
  const computedRawTotal =
    rawTotal ?? Number((nextQuantityNumber * nextPriceAmount).toFixed(2))
  const computedTotal = total ?? computedRawTotal

  const product = {
    ...baseSource,
    ...directSource,
    id,
    productID,
    name,
    pictures,
    quantity: {
      number: nextQuantityNumber,
      unit: nextQuantityUnit,
    },
    price: {
      amount: nextPriceAmount,
      currency: nextPriceCurrency,
    },
    rawPrice: nextPriceAmount,
    rawTotal: computedRawTotal,
    total: computedTotal,
    ...(byUserID ? { byUserID } : {}),
    ...(organizationID ? { organizationID } : {}),
    ...(projectID ? { projectID } : {}),
    ...(placeID ? { placeID } : {}),
    ...(slug ? { slug } : {}),
    ...(action ? { action } : {}),
    ...(nature ? { nature } : {}),
    ...(condition ? { condition } : {}),
    ...(provenance ? { provenance } : {}),
    ...(category ? { category } : {}),
    ...(brand ? { brand } : {}),
    ...(model ? { model } : {}),
    ...(ref ? { ref } : {}),
    ...(dimensions ? { dimensions } : {}),
  } as SerializedOrderProduct

  if (!slug) delete product.slug

  product.rawProduct = buildCanonicalRawProduct(baseSource, product)

  return product
}

const buildSelectionKey = (
  product: OrderSelectionNodeData,
  mode: OrderSelectionMode,
): string => {
  if (mode === 'request') {
    return `${product.byUserID ?? ''}::${product.productID}`
  }
  return product.productID
}

const mergeSelectionProducts = (
  current: OrderSelectionNodeData,
  incoming: OrderSelectionNodeData,
): OrderSelectionNodeData => {
  const quantityNumber =
    Number(current.quantity.number ?? 0) + Number(incoming.quantity.number ?? 0)
  const rawPrice =
    current.rawPrice ||
    incoming.rawPrice ||
    (current.rawPrice !== undefined || incoming.rawPrice !== undefined
      ? 0
      : undefined)
  const priceCurrency = current.price.currency || incoming.price.currency
  const currentSource =
    getSourceRawProduct(current) ?? stripNestedRawProduct(current)
  const incomingSource =
    getSourceRawProduct(incoming) ?? stripNestedRawProduct(incoming)
  const mergedSource = {
    ...incomingSource,
    ...currentSource,
    id: current.id || incoming.id,
    productID: current.productID || incoming.productID,
    name: current.name || incoming.name,
    pictures:
      current.pictures.length > 0 ? current.pictures : incoming.pictures,
    byUserID: current.byUserID ?? incoming.byUserID,
    organizationID: current.organizationID ?? incoming.organizationID,
    projectID: current.projectID ?? incoming.projectID,
    placeID: current.placeID ?? incoming.placeID,
    slug: current.slug ?? incoming.slug,
    action: current.action ?? incoming.action,
    nature: current.nature ?? incoming.nature,
    condition: current.condition ?? incoming.condition,
    provenance: current.provenance ?? incoming.provenance,
    category: current.category ?? incoming.category,
    brand: current.brand ?? incoming.brand,
    model: current.model ?? incoming.model,
    ref: current.ref ?? incoming.ref,
    dimensions: current.dimensions ?? incoming.dimensions,
  } satisfies OrderProductSource

  return buildSelectionProductFromNode({
    source: {
      ...mergedSource,
      quantity: {
        number: quantityNumber,
        unit: current.quantity.unit || incoming.quantity.unit || 'unit',
      },
      price: { amount: rawPrice, currency: priceCurrency },
      rawPrice,
    },
  })
}

const buildSelectionProductFromNode = (params: {
  source: OrderProductSource
  aggregatedQuantity?: AggregatedQuantityData
}): OrderSelectionNodeData => {
  const { source, aggregatedQuantity } = params
  const quantityNumber =
    aggregatedQuantity &&
    typeof aggregatedQuantity['quantity.number'] === 'number'
      ? aggregatedQuantity['quantity.number']
      : undefined
  const quantityUnit =
    aggregatedQuantity &&
    typeof aggregatedQuantity['quantity.unit'] === 'string' &&
    aggregatedQuantity['quantity.unit'].length > 0
      ? aggregatedQuantity['quantity.unit']
      : undefined

  const product = buildCanonicalOrderProduct({
    source,
    quantityNumber,
    quantityUnit,
  })
  // Selection is transient: retain an absent amount until the editor applies
  // hydrated defaults. Explicit amounts, including zero, retain legacy EUR.
  const amount = readPriceAmount(source)
  const price = {
    amount,
    currency:
      readPriceCurrency(source) ?? (amount !== undefined ? 'eur' : undefined),
  }
  const selected = {
    ...product,
    price,
    rawPrice: amount,
    rawTotal: amount === undefined ? undefined : product.rawTotal,
    total: amount === undefined ? undefined : product.total,
  }
  return {
    ...selected,
    rawProduct: {
      ...product.rawProduct,
      price,
      rawPrice: selected.rawPrice,
      rawTotal: selected.rawTotal,
      total: selected.total,
    },
  }
}

export const buildOrderSelectionProductsFromNodes = (params: {
  nodes: OrderSelectionNode[]
  mode: OrderSelectionMode
}): OrderSelectionNodeData[] => {
  const { nodes, mode } = params
  const productsByKey = new Map<string, OrderSelectionNodeData>()
  const seenLeafIds = new Set<string>()
  const parentAggregateKeys = new Set<string>()

  nodes.forEach((node) => {
    if (node.group || !node.data) return
    if (!node.parent?.aggData || (node.childIndex ?? 0) !== 0) return
    const product = buildSelectionProductFromNode({
      source: node.data,
      aggregatedQuantity: node.parent.aggData,
    })
    if (!product.id || !product.productID) return
    parentAggregateKeys.add(buildSelectionKey(product, mode))
  })

  nodes.forEach((node) => {
    if (node.group && Array.isArray(node.allLeafChildren)) {
      node.allLeafChildren.forEach((leaf) => {
        const source = leaf?.data
        if (!isRecord(source)) return
        const product = buildSelectionProductFromNode({ source })
        if (!product.id || !product.productID || seenLeafIds.has(product.id)) {
          return
        }
        seenLeafIds.add(product.id)
        const key = buildSelectionKey(product, mode)
        const current = productsByKey.get(key)
        productsByKey.set(
          key,
          current ? mergeSelectionProducts(current, product) : product,
        )
      })
      return
    }

    if (!isRecord(node.data)) return
    const candidate = buildSelectionProductFromNode({
      source: node.data,
      aggregatedQuantity:
        node.parent?.aggData && (node.childIndex ?? 0) === 0
          ? node.parent.aggData
          : undefined,
    })
    if (!candidate.id || !candidate.productID) return
    const key = buildSelectionKey(candidate, mode)
    if (
      parentAggregateKeys.has(key) &&
      node.parent?.aggData &&
      (node.childIndex ?? 0) > 0
    ) {
      return
    }
    if (seenLeafIds.has(candidate.id)) return
    seenLeafIds.add(candidate.id)
    const current = productsByKey.get(key)
    productsByKey.set(
      key,
      current ? mergeSelectionProducts(current, candidate) : candidate,
    )
  })

  return Array.from(productsByKey.values())
}

export const buildOrderSelectionProductsFromApi = (params: {
  api: OrderSelectionApi
  mode: OrderSelectionMode
}): OrderSelectionNodeData[] =>
  buildOrderSelectionProductsFromNodes({
    nodes: params.api.getSelectedNodes() ?? [],
    mode: params.mode,
  })

export const buildOrderSelectionProductsFromRows = (params: {
  rows: Array<OrderProductSource | InventoryRow>
  mode: OrderSelectionMode
}): OrderSelectionNodeData[] =>
  buildOrderSelectionProductsFromNodes({
    nodes: params.rows.map((row) => ({ data: row })),
    mode: params.mode,
  })

export const createOrderSelectionApi = (
  products: OrderSelectionNodeData[],
): OrderSelectionApi => ({
  getSelectedNodes: () =>
    products.map((product) => ({
      level: 0,
      childIndex: 0,
      data: product,
    })),
})

export const buildOrderModalItemFromProduct = (params: {
  product: OrderSelectionNodeData
  fallbackByUserID?: string
  fallbackPriceCurrency?: string
}): OrderModalItem => {
  const { product, fallbackByUserID, fallbackPriceCurrency } = params
  const quantity = Number(product.quantity.number ?? 0) || 0
  const rawPrice = Number(product.rawPrice ?? product.price.amount ?? 0) || 0

  return {
    id: product.id,
    productID: product.productID,
    byUserID: product.byUserID || fallbackByUserID,
    name: product.name,
    pictures: product.pictures,
    remainingQuantity: quantity,
    quantity,
    currency: product.price.currency || fallbackPriceCurrency,
    rawPrice,
    rawTotal: Number((quantity * rawPrice).toFixed(2)),
    price: rawPrice,
    total: Number((quantity * rawPrice).toFixed(2)),
    unit: product.quantity.unit,
    unitLabel: product.quantity.unit,
    weight: readNumberValue(product.weight),
    condition: product.condition,
    provenance: product.provenance,
    rawProduct: product.rawProduct ?? stripNestedRawProduct(product),
    nature: product.nature,
    category: product.category,
    brand: product.brand,
    model: product.model,
    ref: product.ref,
    dimensions: getOrderProductDimensionsLabel(product),
  }
}

export const serializeOrderLineItem = (params: {
  item: OrderModalItem
  currency?: string
  fallbackByUserID?: string
}): SerializedOrderLineItem => {
  const { item, currency, fallbackByUserID } = params
  const quantity = Number(item.quantity ?? 0) || 0
  const rawPrice = Number(item.price ?? item.rawPrice ?? 0) || 0
  const rawTotal =
    Number(item.rawTotal ?? item.total ?? quantity * rawPrice) || 0

  const source = {
    ...(isRecord(item.rawProduct) ? item.rawProduct : {}),
    id: item.id,
    productID: item.productID,
    name: item.name,
    pictures: item.pictures ?? [],
    quantity: {
      number: quantity,
      unit: item.unit ?? 'unit',
    },
    price: {
      amount: rawPrice,
      currency: item.currency || currency || 'eur',
    },
    rawPrice,
    rawTotal,
    total: Number(item.total ?? rawTotal) || rawTotal,
    byUserID: item.byUserID || fallbackByUserID,
    condition: item.condition,
    provenance: item.provenance,
    ...(item.nature ? { nature: item.nature } : {}),
    ...(item.category ? { category: item.category } : {}),
    ...(item.brand ? { brand: item.brand } : {}),
    ...(item.model ? { model: item.model } : {}),
    ...(item.ref ? { ref: item.ref } : {}),
    ...(item.dimensions ? { dimensions: item.dimensions } : {}),
  } satisfies OrderProductSource

  const product = buildCanonicalOrderProduct({
    source,
    quantityNumber: quantity,
    quantityUnit: item.unit ?? 'unit',
    priceAmount: rawPrice,
    priceCurrency: currency || readPriceCurrency(source),
    rawTotal,
    total: Number(item.total ?? rawTotal) || rawTotal,
    fallbackByUserID,
  })

  return {
    product,
    quantity,
  }
}
